/* Netlify Identity, spoken to directly.
 *
 * The official widget would work, but it renders its own Netlify-branded modal
 * over a page that has been designed down to the line height. This is the same
 * GoTrue API underneath, in about a hundred lines, so the sign-in screen can
 * belong to the app.
 *
 * The session lives in localStorage and is read *synchronously*. That matters:
 * with an account required, deciding whether to show the app must not depend on
 * reaching the network, or every flight becomes a login wall.
 */

import { api } from './desktop.js';

const BASE = api('/.netlify/identity');
const KEY = 'longhand-session-v1';
const REFRESH_MARGIN_MS = 60_000;

const listeners = new Set();
let session = read();
let lastSeeded = null;
let refreshing = null;

/* ------------------------------------------------------------- storage */

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s?.access_token && s?.user?.id ? s : null;
  } catch (e) { return null; }
}

function write(next) {
  session = next;
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch (e) {}
  // Subscribers are handed the *user*, not the session. Every caller wants to
  // know who is signed in, and a session object has no `id` on it — passing one
  // where a user is expected reads as "signed in as nobody", which is how you
  // end up opening a database called `longhand-undefined`.
  for (const fn of listeners) fn(next?.user || null);
}

/** Read the `sub` and `exp` out of a JWT without a round trip. */
function claims(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, '=')));
  } catch (e) { return {}; }
}

/* -------------------------------------------------------------- errors */

/**
 * GoTrue reports failures in three different shapes depending on the route.
 * A 404 only means a dead link when a link was what we followed; anywhere else
 * it means the request never found Identity at all.
 */
async function fault(res, { link = false } = {}) {
  let detail = '';
  try {
    const body = await res.json();
    detail = body.error_description || body.msg || body.error || '';
  } catch (e) {}
  if (res.status === 400 && /grant|credentials|password/i.test(detail)) {
    return new Error('That email and password do not match.');
  }
  if (res.status === 422 && /registered|exists/i.test(detail)) {
    return new Error('There is already an account with that email.');
  }
  if (res.status === 400 && /not confirmed/i.test(detail)) {
    return new Error('This account still needs confirming — open the link in the email we sent.');
  }
  if (res.status === 403) return new Error('Sign-ups are invite-only on this site.');
  if (link && (res.status === 404 || res.status === 410)) {
    return new Error('That link has expired or was already used. Request a new one below.');
  }
  return new Error(detail || 'Could not reach the sign-in service.');
}

/* --------------------------------------------------------------- calls */

async function grant(body) {
  const res = await fetch(BASE + '/token', {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body)
  });
  if (!res.ok) {
    const err = await fault(res);
    // A refused grant is the server saying no; anything else may just be a bad
    // connection, and must not be treated as a revoked session.
    err.rejected = res.status === 400 || res.status === 401;
    throw err;
  }
  return res.json();
}

function store(token) {
  const { sub, email, exp } = claims(token.access_token);
  write({
    access_token: token.access_token,
    refresh_token: token.refresh_token,
    expires_at: exp ? exp * 1000 : Date.now() + (token.expires_in || 3600) * 1000,
    user: { id: sub, email: email || '' }
  });
  return session;
}

export function currentUser() { return session?.user || null; }

/** Called with `{id, email}` on sign-in and token refresh, `null` on sign-out. */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function login(email, password) {
  return store(await grant({ grant_type: 'password', username: email, password }));
}

export async function signup(email, password) {
  const res = await fetch(BASE + '/signup', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!res.ok) throw await fault(res);
  const body = await res.json();

  // With confirmation enabled there is no session yet — the account exists but
  // the email has to be clicked first. Say so rather than failing at login.
  if (!body.confirmed_at && !body.access_token) return { confirmationRequired: true };
  return login(email, password).then(() => ({ confirmationRequired: false }));
}

export async function recover(email) {
  const res = await fetch(BASE + '/recover', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!res.ok) throw await fault(res);
}

export function logout() {
  const token = session?.access_token;
  write(null);
  // Best effort: the local session is already gone, which is what matters.
  if (token) {
    fetch(BASE + '/logout', { method: 'POST', headers: { Authorization: 'Bearer ' + token } })
      .catch(() => {});
  }
}

/**
 * A usable bearer token, refreshed if it is close to expiry.
 *
 * Throws offline rather than signing the user out: a dead network is not a
 * revoked session, and clearing the session would take their writing off screen
 * for the length of a tunnel. Only a refresh the server actively rejects ends
 * the session.
 */
export async function authorization() {
  if (!session) throw new Error('Not signed in.');
  if (Date.now() < session.expires_at - REFRESH_MARGIN_MS) {
    return 'Bearer ' + session.access_token;
  }

  // GoTrue rotates refresh tokens: using one spends it. Two overlapping calls
  // would each try, and the second would present a token that no longer exists
  // — a rejection, which reads as a revoked session and signs the user out for
  // no reason. One refresh at a time, shared by every caller waiting on it.
  if (!refreshing) {
    refreshing = grant({ grant_type: 'refresh_token', refresh_token: session.refresh_token })
      .then(store)
      .finally(() => { refreshing = null; });
  }

  try {
    await refreshing;
  } catch (err) {
    if (err.rejected) { write(null); throw new Error('Your session expired. Sign in again.'); }
    throw err;
  }
  return 'Bearer ' + session.access_token;
}

/* ------------------------------------------------------- email callbacks */

/* Netlify mails confirmation, invite and recovery links back to the site with
   the token in the URL *fragment*, and nothing consumes it on arrival. The
   official widget does this; talking to GoTrue directly means doing it here.
   Miss it and a confirmed-looking click leaves the account unconfirmed, with
   the only symptom appearing later, at sign-in. */
const TOKEN_KINDS = [
  ['confirmation_token', 'signup'],
  ['invite_token', 'invite'],
  ['recovery_token', 'recovery'],
  ['email_change_token', 'email_change']
];

/**
 * Handle a link arrived at from an email. Returns what happened, or null when
 * this is an ordinary page load.
 */
export async function consumeUrlToken(loc = globalThis.location, hist = globalThis.history) {
  const raw = (loc?.hash || '').replace(/^#/, '');
  if (!raw) return null;
  const params = new URLSearchParams(raw);

  // Clear the fragment either way: a token must never survive in the address
  // bar, in history, or in anything the user might paste.
  const clear = () => {
    try { hist.replaceState(null, '', loc.pathname + loc.search); } catch (e) {}
  };

  const failed = params.get('error_description') || params.get('error');
  if (failed) {
    clear();
    return { kind: 'error', message: decodeURIComponent(failed.replace(/\+/g, ' ')) };
  }

  for (const [param, type] of TOKEN_KINDS) {
    const token = params.get(param);
    if (!token) continue;
    clear();
    try {
      const res = await fetch(BASE + '/verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ type, token })
      });
      if (!res.ok) throw await fault(res, { link: true });
      store(await res.json());
      return { kind: type };
    } catch (err) {
      return { kind: 'error', type, message: err.message };
    }
  }
  return null;
}

/** Set a new password for the signed-in user. Used after a recovery link. */
export async function updatePassword(password) {
  const res = await fetch(BASE + '/user', {
    method: 'PUT',
    headers: { 'content-type': 'application/json', Authorization: await authorization() },
    body: JSON.stringify({ password })
  });
  if (!res.ok) throw await fault(res);
  return res.json();
}

/* ------------------------------------------------------------ transport */

/** The sync transport, carrying the Identity token. */
export function createTransport(fetchImpl = fetch) {
  async function call(payload) {
    const res = await fetchImpl(api('/api/sync'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', Authorization: await authorization() },
      body: JSON.stringify(payload)
    });
    if (res.status === 401) {
      // Deliberately not signing out. A 401 here can mean a misconfigured
      // function or a deploy mid-flight as easily as a bad token, and throwing
      // someone out of their writing over a failed background request is the
      // worst possible response. The loop reports it; the session stands or
      // falls on whether Identity itself rejects a refresh.
      const err = new Error('The server would not accept this session. Your writing is safe here.');
      err.status = 401;
      throw err;
    }
    if (!res.ok) {
      let msg = 'Sync failed.';
      try { msg = (await res.json()).error || msg; } catch (e) {}
      throw new Error(msg);
    }
    return res.json();
  }

  return {
    async pull(cursor) {
      const { records, cursor: next, seeded } = await call({ cursor, pull: true });
      lastSeeded = seeded;
      return { records, cursor: next };
    },
    async push(records) {
      // `pull: false` keeps this to a write. Without it the endpoint would
      // answer with a fresh page from cursor 0 on every push.
      const { seeded } = await call({ records, pull: false });
      lastSeeded = seeded;
      // The cursor deliberately does not move on a push — see src/sync.js.
      return {};
    },
    /** Whether the account already held content, as of the last exchange. */
    get seeded() { return lastSeeded; }
  };
}
